import {
    Flex,
    Heading,
    Text,
    useColorModeValue,
    Avatar,
    Stack,
    Icon,
    Tag,
    IconButton,
    Tooltip,
    Box,
    Input,
    Button,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter,
    useDisclosure,
    Textarea,
    useToast,
    Alert,
    AlertIcon,
    AlertTitle,
    AlertDescription,
    FormControl,
    FormLabel,
    Select,
    VStack,
    HStack,
    Divider,
} from '@chakra-ui/react';
import { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import {
    FiChevronLeft,
    FiChevronRight,
    FiChevronsLeft,
    FiChevronsRight,
} from 'react-icons/fi';
import '../../theme/solarizedTheme';
import { Loading } from '../../helpers/Loading';
import { customStyles } from '../../helpers/customStyles';
// import DownloadStudentReport from './StudentReport';
import { getMateria, updateMateria } from '../../features/materiaSlice';
import { getAllMatriculasByCurso, reset as resetMatriculas } from '../../features/matriculaSlice';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import { FaFileExcel, FaGraduationCap } from 'react-icons/fa';
import { MdEdit, MdDelete } from 'react-icons/md';
import { getAllConfiguraciones } from '../../features/configuracionSlice';
import CustomBackRoute from '../../helpers/CustomBackRoute';
import { AddIcon } from '@chakra-ui/icons';
import { getActiveAcademicYear } from '../../features/academicYearSlice';
import { saveAllCalificaciones, getAllNotasPorMateria, reset as resetNotas } from '../../features/notaSlice';

const RegistroCalificacionComportamental = () => {
    const dispatch = useDispatch();
    const params = useParams();
    const toast = useToast();
    const themeTable = useColorModeValue('default', 'solarized');

    // Redux selectors
    const { materia } = useSelector(state => state.materias);
    const { notasByMateria, isLoading, isLoadingSave } = useSelector(state => state.calificaciones);
    const { sedeSeleccionada, user } = useSelector(state => state.auth);
    const { configuracion } = useSelector(state => state.configuraciones);
    const { active_academic_year } = useSelector(state => state.academic_year);

    // Local state
    const [search, setSearch] = useState('');
    const [notasData, setNotasData] = useState({});
    const [hasChanges, setHasChanges] = useState(false);

    // Limpiar estado cuando cambia la materia
    useEffect(() => {
        // Limpiar completamente el estado local
        setNotasData({});
        setSearch('');
        setHasChanges(false);
        
        // Limpiar el estado de Redux (matrículas y notas)
        dispatch(resetMatriculas());
        dispatch(resetNotas());
    }, [params.id, dispatch]);

    // Cargar datos iniciales
    useEffect(() => {
        if (params.id) {
            // Pequeño delay para asegurar que el reset se complete
            const timer = setTimeout(() => {
                dispatch(getMateria(params.id));
                dispatch(getAllMatriculasByCurso(params.id));
                dispatch(getAllConfiguraciones());
                dispatch(getActiveAcademicYear());
            }, 50);

            return () => clearTimeout(timer);
        }
    }, [dispatch, params.id, sedeSeleccionada._id]);

    // Cargar notas cuando la materia esté lista
    useEffect(() => {
        if (materia?._id && materia?.grado?._id) {
            dispatch(getAllNotasPorMateria({ materia: materia._id, grado: materia.grado._id }));
        }
    }, [dispatch, materia?._id, materia?.grado?._id]);

    // Inicializar notasData con datos del servidor
    useEffect(() => {
        if (notasByMateria?.length >= 0) {
            const initialData = notasByMateria.reduce((acc, row) => {
                acc[row.matriculaId] = {
                    matriculaId: row.matriculaId,
                    observaciones: row.observaciones || '',
                    valoracion1: row.valoracion1 || '',
                    valoracion2: row.valoracion2 || '',
                    valoracion3: row.valoracion3 || '',
                    valoracion4: row.valoracion4 || '',
                };
                return acc;
            }, {});
            setNotasData(initialData);
            setHasChanges(false);
        }
    }, [notasByMateria]);

    // Verificar cambios
    const hasDataChanged = useMemo(() => {
        return Object.entries(notasData).some(([key, values]) => {
            const originalRow = notasByMateria.find(row => row.matriculaId === key);
            if (!originalRow) return true;

            return (
                values.observaciones !== (originalRow.observaciones || '') ||
                values.valoracion1 !== (originalRow.valoracion1 || '') ||
                values.valoracion2 !== (originalRow.valoracion2 || '') ||
                values.valoracion3 !== (originalRow.valoracion3 || '') ||
                values.valoracion4 !== (originalRow.valoracion4 || '')
            );
        });
    }, [notasData, notasByMateria]);

    // Guardar todos los cambios
    const handleSaveAll = async () => {
        const changedData = Object.entries(notasData)
            .filter(([key, values]) => {
                const originalRow = notasByMateria.find(row => row.matriculaId === key);
                if (!originalRow) return true;

                return (
                    values.observaciones !== (originalRow.observaciones || '') ||
                    values.valoracion1 !== (originalRow.valoracion1 || '') ||
                    values.valoracion2 !== (originalRow.valoracion2 || '') ||
                    values.valoracion3 !== (originalRow.valoracion3 || '') ||
                    values.valoracion4 !== (originalRow.valoracion4 || '')
                );
            })
            .map(([key, values]) => ({
                matricula: values.matriculaId || key,
                materia: params.id,
                profesor: user?.usuario?.id || null,
                observaciones: values.observaciones || '',
                valoracion1: values.valoracion1 || '',
                valoracion2: values.valoracion2 || '',
                valoracion3: values.valoracion3 || '',
                valoracion4: values.valoracion4 || '',
            }));

        if (changedData.length === 0) {
            toast({
                title: 'Sin cambios',
                description: 'No hay modificaciones que guardar',
                status: 'info',
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        try {
            const result = await dispatch(saveAllCalificaciones({ notas: changedData })).unwrap();

            toast({
                title: 'Éxito',
                description: `Se guardaron ${changedData.length} registro(s) correctamente`,
                status: 'success',
                duration: 3000,
                isClosable: true,
            });

            if (result.errores?.length > 0) {
                toast({
                    title: 'Advertencias',
                    description: `${result.errores.length} registro(s) con errores`,
                    status: 'warning',
                    duration: 5000,
                    isClosable: true,
                });
            }

            setHasChanges(false);
            dispatch(getAllNotasPorMateria({ materia: params.id, grado: materia?.grado?._id }));

        } catch (error) {
            toast({
                title: 'Error',
                description: error.message || 'Error al guardar las calificaciones',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }
    };

    // Actualizar un campo específico
    const updateNotaField = (matriculaId, field, value) => {
        setNotasData(prev => ({
            ...prev,
            [matriculaId]: {
                ...prev[matriculaId],
                matriculaId,
                [field]: value,
            },
        }));
        setHasChanges(true);
    };

    // Limpiar cambios
    const handleClearChanges = () => {
        const initialData = notasByMateria.reduce((acc, row) => {
            acc[row.matriculaId] = {
                matriculaId: row.matriculaId,
                observaciones: row.observaciones || '',
                valoracion1: row.valoracion1 || '',
                valoracion2: row.valoracion2 || '',
                valoracion3: row.valoracion3 || '',
                valoracion4: row.valoracion4 || '',
            };
            return acc;
        }, {});
        setNotasData(initialData);
        setHasChanges(false);
        
        toast({
            title: 'Cambios descartados',
            description: 'Se han revertido todos los cambios pendientes',
            status: 'info',
            duration: 3000,
            isClosable: true,
        });
    };

    // Filtrar datos
    const filteredData = useMemo(() => {
        return notasByMateria.filter(row =>
            `${row.estudiante?.nombres} ${row.estudiante?.apellidos}`
                .toLowerCase()
                .includes(search.toLowerCase())
        );
    }, [notasByMateria, search]);

    // Exportar a Excel
    const handleExportExcel = () => {
        if (!notasByMateria || notasByMateria.length === 0) {
            toast({
                title: 'Sin datos',
                description: 'No hay datos para exportar',
                status: 'warning',
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        const currentPeriodo = Number(active_academic_year?.periodo) || 1;
        const valoracionField = `valoracion${currentPeriodo}`;
        
        const worksheet = XLSX.utils.json_to_sheet(
            notasByMateria.map(row => {
                const valoracionText = row[valoracionField] || 'Sin valoración';
                
                return {
                    'N°': notasByMateria.indexOf(row) + 1,
                    'Nombres': `${row.estudiante?.nombres} ${row.estudiante?.apellidos}`,
                    'Valoración Comportamental': valoracionText,
                    'Observaciones': row.observaciones || '',
                };
            })
        );

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Estudiantes');
        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const data = new Blob([excelBuffer], { type: 'application/octet-stream' });
        saveAs(data, `${materia?.nombre || 'Calificaciones'}.xlsx`);
    };

    // Columnas de la tabla
    const columns = useMemo(() => [
        {
            name: 'N°',
            selector: row => notasByMateria?.indexOf(row) + 1,
            sortable: true,
            width: '70px',
        },
        {
            name: 'NOMBRES',
            selector: row => `${row.estudiante?.apellidos} ${row.estudiante?.nombres}`,
            sortable: true,
            cell: row => (
                <Stack spacing={2} direction="row" align="center">
                    <Avatar
                        size="sm"
                        color="white"
                        name={`${row.estudiante?.apellidos} ${row.estudiante?.nombres}`}
                        src={row?.img}
                        display={{ base: 'none', lg: 'flex' }}
                    />
                    <Text fontSize="12px" noOfLines={2}>
                        {row.estudiante?.apellidos} {row.estudiante?.nombres}
                    </Text>
                </Stack>
            ),
        },
        {
            name: 'DESCRIPCIÓN COMPORTAMENTAL',
            cell: row => (
                <ModalDescripcionComportamental
                    data={row}
                    notasData={notasData}
                    onUpdate={updateNotaField}
                    materia={materia}
                />
            ),
            center: true,
            width: '400px',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
        },
        {
            name: 'OBSERVACIONES',
            cell: row => (
                <ModalObservaciones
                    data={row}
                    notasData={notasData}
                    onUpdate={updateNotaField}
                />
            ),
            center: true,
        }
    ], [materia, notasData, configuracion]);

    if (isLoading || !materia?._id) {
        return <Loading />;
    }

    return (
        <Box
            key={params.id}
            borderTop="8px solid"
            borderColor={materia?.brand_color || '#000000'}
            borderRadius="2xl"
            bg="white"
            boxShadow="base"
            _dark={{ bg: 'primary.1000', color: 'white' }}
            p={6}
        >
            {/* Header */}
            <Flex justify="space-between" align="center" mb={4}>
                <CustomBackRoute />
                <Stack spacing={2} ml={2} w="full" direction="row" justify="space-between" align="center">
                    <Heading size="md">{materia?.nombre || 'Cargando...'}</Heading>
                    <Stack direction="row" spacing={2}>
                        <ModalGestionDescripciones materia={materia} />
                        <Tag colorScheme="purple" px={4} variant="outline">
                            <Icon as={FaGraduationCap} mr={2} boxSize={4} />
                            GRADO: {materia?.grado?.nombre || 'N/A'}
                        </Tag>
                    </Stack>
                </Stack>
            </Flex>

            {/* Search and actions */}
            <Stack direction={['column', 'column', 'row']} justify="space-between" spacing={4} mb={4}>
                <Input
                    placeholder="Buscar estudiante..."
                    variant="filled"
                    size="lg"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    focusBorderColor="primary.100"
                />
                <Stack direction="row" spacing={2}>
                    <Tooltip label="Exportar a Excel">
                        <IconButton
                            icon={<FaFileExcel size={24} />}
                            size="lg"
                            colorScheme="green"
                            onClick={handleExportExcel}
                        />
                    </Tooltip>
                    {/* <DownloadStudentReport id={params.id} /> */}
                </Stack>
            </Stack>

            {/* Alert cambios pendientes */}
            {hasDataChanged && (
                <Alert status="info" mb={4} borderRadius="md">
                    <AlertIcon />
                    <AlertTitle>Cambios pendientes</AlertTitle>
                    <AlertDescription>
                        Tienes modificaciones sin guardar. Haz clic en "GUARDAR CAMBIOS".
                    </AlertDescription>
                </Alert>
            )}

            {/* Period info and actions */}
            <Stack mb={4} direction={['column', 'row']} justify="space-between" align="center">
                <Heading size="sm" fontWeight="normal">
                    PERIODO ACADÉMICO: 
                    <Text as="span" fontWeight="bold" ml={2}>
                        {active_academic_year?.year} - {active_academic_year?.periodo}
                    </Text>
                </Heading>
                <Stack direction="row" spacing={2}>
                    <Button
                        size="sm"
                        colorScheme="red"
                        variant="outline"
                        onClick={handleClearChanges}
                        isDisabled={!hasDataChanged}
                    >
                        DESCARTAR CAMBIOS
                    </Button>
                    <Button
                        size="sm"
                        colorScheme="primary"
                        onClick={handleSaveAll}
                        isLoading={isLoadingSave}
                        loadingText="Guardando..."
                        isDisabled={!hasDataChanged}
                    >
                        GUARDAR CAMBIOS
                    </Button>
                </Stack>
            </Stack>

            {/* Data table */}
            <DataTable
                columns={columns}
                data={filteredData}
                pagination
                paginationIconFirstPage={<Icon as={FiChevronsLeft} boxSize={6} />}
                paginationIconLastPage={<Icon as={FiChevronsRight} boxSize={6} />}
                paginationIconPrevious={<Icon as={FiChevronLeft} boxSize={6} />}
                paginationIconNext={<Icon as={FiChevronRight} boxSize={6} />}
                paginationRowsPerPageOptions={[10, 25, 50, 100]}
                paginationPerPage={100}
                paginationComponentOptions={{
                    rowsPerPageText: 'Filas por página:',
                    rangeSeparatorText: 'de',
                    selectAllRowsItem: true,
                    selectAllRowsItemText: 'Todos',
                }}
                theme={themeTable}
                customStyles={customStyles}
                responsive
                noDataComponent={<Text my={4} fontSize="lg">NO HAY REGISTROS</Text>}
            />
        </Box>
    );
};

export default RegistroCalificacionComportamental;

// Componente Modal para Gestionar Descripciones Comportamentales
function ModalGestionDescripciones({ materia }) {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const dispatch = useDispatch();
    const toast = useToast();
    const [descripciones, setDescripciones] = useState([]);
    const [nuevaDescripcion, setNuevaDescripcion] = useState('');
    const [editingId, setEditingId] = useState(null);
    const [editingText, setEditingText] = useState('');

    useEffect(() => {
        if (isOpen && materia?.descripcionComportamental) {
            setDescripciones(materia.descripcionComportamental);
        }
    }, [isOpen, materia]);

    const handleAgregar = () => {
        if (!nuevaDescripcion.trim()) {
            toast({
                title: 'Campo vacío',
                description: 'Debes escribir una descripción',
                status: 'warning',
                duration: 2000,
            });
            return;
        }

        if (descripciones.length >= 4) {
            toast({
                title: 'Límite alcanzado',
                description: 'Solo puedes tener máximo 4 descripciones',
                status: 'warning',
                duration: 2000,
            });
            return;
        }

        const newId = descripciones.length > 0 ? Math.max(...descripciones.map(d => d.id)) + 1 : 1;
        setDescripciones([...descripciones, { id: newId, text: nuevaDescripcion.trim() }]);
        setNuevaDescripcion('');
    };

    const handleEditar = (id) => {
        const desc = descripciones.find(d => d.id === id);
        setEditingId(id);
        setEditingText(desc.text);
    };

    const handleGuardarEdicion = () => {
        if (!editingText.trim()) {
            toast({
                title: 'Campo vacío',
                description: 'La descripción no puede estar vacía',
                status: 'warning',
                duration: 2000,
            });
            return;
        }

        setDescripciones(descripciones.map(d => 
            d.id === editingId ? { ...d, text: editingText.trim() } : d
        ));
        setEditingId(null);
        setEditingText('');
    };

    const handleEliminar = (id) => {
        setDescripciones(descripciones.filter(d => d.id !== id));
    };

    const handleGuardarTodo = async () => {
        try {
            dispatch(updateMateria({ ...materia, descripcionComportamental: descripciones }));
            onClose();
            dispatch(getMateria(materia._id));
        } catch (error) {
            toast({
                title: 'Error',
                description: 'No se pudieron guardar las descripciones',
                status: 'error',
                duration: 3000,
            });
        }
    };

    return (
        <>
            <Button
                colorScheme="pink"
                onClick={onOpen}
                size="sm"
                variant="solid"
                leftIcon={<MdEdit size={24} />}
            >
                Gestionar descripciones
            </Button>

            <Modal isOpen={isOpen} onClose={onClose} size="4xl">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Gestionar Descripciones Comportamentales</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <VStack spacing={4} align="stretch">
                            {/* Agregar nueva descripción */}
                            <Box>
                                <FormLabel>Agregar nueva descripción (Máximo 4)</FormLabel>
                                <HStack>
                                    <Input
                                        placeholder="Ej: Excelente comportamiento y participación"
                                        value={nuevaDescripcion}
                                        onChange={e => setNuevaDescripcion(e.target.value)}
                                        onKeyPress={e => e.key === 'Enter' && handleAgregar()}
                                        isDisabled={descripciones.length >= 4}
                                    />
                                    <Button
                                        colorScheme="pink"
                                        onClick={handleAgregar}
                                        isDisabled={descripciones.length >= 4}
                                    >
                                        Agregar
                                    </Button>
                                </HStack>
                                <Text fontSize="xs" color="gray.500" mt={1}>
                                    {descripciones.length}/4 descripciones
                                </Text>
                            </Box>

                            <Divider />

                            {/* Lista de descripciones */}
                            <Box>
                                <FormLabel>Descripciones actuales</FormLabel>
                                {descripciones.length === 0 ? (
                                    <Text color="gray.500" fontSize="sm">No hay descripciones agregadas</Text>
                                ) : (
                                    <VStack spacing={2} align="stretch">
                                        {descripciones.map((desc) => (
                                            <Box
                                                key={desc.id}
                                                p={3}
                                                borderRadius="md"
                                                border="1px"
                                                borderColor="gray.200"
                                                _dark={{ borderColor: 'gray.600' }}
                                            >
                                                {editingId === desc.id ? (
                                                    <HStack>
                                                        <Input
                                                            value={editingText}
                                                            onChange={e => setEditingText(e.target.value)}
                                                            size="sm"
                                                            maxLength={100}
                                                        />
                                                        <Button
                                                            size="sm"
                                                            colorScheme="pink"
                                                            onClick={handleGuardarEdicion}
                                                        >
                                                            Guardar
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            onClick={() => setEditingId(null)}
                                                        >
                                                            Cancelar
                                                        </Button>
                                                    </HStack>
                                                ) : (
                                                    <HStack justify="space-between">
                                                        <Text fontSize="sm">{desc.text}</Text>
                                                        <HStack>
                                                            <IconButton
                                                                icon={<MdEdit />}
                                                                size="sm"
                                                                colorScheme="pink"
                                                                variant="ghost"
                                                                onClick={() => handleEditar(desc.id)}
                                                            />
                                                            <IconButton
                                                                icon={<MdDelete />}
                                                                size="sm"
                                                                colorScheme="pink"
                                                                variant="ghost"
                                                                onClick={() => handleEliminar(desc.id)}
                                                            />
                                                        </HStack>
                                                    </HStack>
                                                )}
                                            </Box>
                                        ))}
                                    </VStack>
                                )}
                            </Box>
                        </VStack>
                    </ModalBody>
                    <ModalFooter>
                        <Button colorScheme="pink" mr={3} onClick={handleGuardarTodo}>
                            Guardar Cambios
                        </Button>
                        <Button variant="outline" onClick={onClose}>Cancelar</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </>
    );
}

// Componente Modal de Descripción Comportamental
function ModalDescripcionComportamental({ data, notasData, onUpdate, materia }) {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [selectedDescripcion, setSelectedDescripcion] = useState('');
    const { active_academic_year } = useSelector(state => state.academic_year);

    const currentPeriodo = Number(active_academic_year?.periodo) || 1;
    const valoracionField = `valoracion${currentPeriodo}`;

    useEffect(() => {
        if (isOpen) {
            const descripcionText = notasData[data.matriculaId]?.[valoracionField] || '';
            // Si hay un texto guardado, buscar el ID correspondiente
            if (descripcionText) {
                const descripcion = materia?.descripcionComportamental?.find(v => v.text === descripcionText);
                setSelectedDescripcion(descripcion?.id?.toString() || '');
            } else {
                setSelectedDescripcion('');
            }
        }
    }, [isOpen, notasData, data.matriculaId, valoracionField, materia]);

    const handleSave = () => {
        // Guardar el texto de la descripción en lugar del ID
        const descripcionText = materia?.descripcionComportamental?.find(v => String(v.id) === String(selectedDescripcion))?.text || selectedDescripcion;
        onUpdate(data.matriculaId, valoracionField, descripcionText.trim());
        onClose();
    };

    const descripcionActual = notasData[data.matriculaId]?.[valoracionField];
    const hasDescripcion = Boolean(descripcionActual?.trim());

    return (
        <>
            <Tooltip label={`${hasDescripcion ? 'Cambiar' : 'Seleccionar'} descripción - Periodo ${currentPeriodo}`}>
                <Tag
                    size="md"
                    variant="solid"
                    colorScheme={hasDescripcion ? 'purple' : 'gray'}
                    cursor="pointer"
                    onClick={onOpen}
                    _hover={{ opacity: 0.8 }}
                    py={1}
                    maxW="800px"
                    lineHeight="1.3"
                    fontSize="xs"
                >
                    {hasDescripcion ? descripcionActual : 'Sin descripción'}
                </Tag>
            </Tooltip>

            <Modal isOpen={isOpen} onClose={onClose} size="4xl">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>
                        Descripción Comportamental - Periodo {currentPeriodo}
                        <Text fontSize="sm" color="gray.500" fontWeight="normal">
                            {data.estudiante?.nombres} {data.estudiante?.apellidos}
                        </Text>
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <FormControl>
                            <FormLabel>Selecciona la descripción del estudiante</FormLabel>
                            <Select
                                value={selectedDescripcion}
                                onChange={e => setSelectedDescripcion(e.target.value)}
                                placeholder="Selecciona una descripción"
                                size="lg"
                            >
                                {materia?.descripcionComportamental?.map((descripcion) => (
                                    <option key={descripcion.id} value={descripcion.id}>
                                        {descripcion.text}
                                    </option>
                                ))}
                            </Select>
                        </FormControl>

                        {selectedDescripcion && (
                            <Box mt={4} p={3} borderRadius="md" bg="gray.50" _dark={{ bg: 'gray.700' }}>
                                <Text fontSize="sm" fontWeight="semibold" mb={2}>
                                    Descripción seleccionada:
                                </Text>
                                <Tag
                                    size="lg"
                                    variant="solid"
                                    colorScheme="purple"
                                >
                                    {materia?.descripcionComportamental?.find(v => String(v.id) === String(selectedDescripcion))?.text || selectedDescripcion}
                                </Tag>
                            </Box>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button colorScheme="purple" mr={3} onClick={handleSave}>
                            Guardar
                        </Button>
                        <Button variant="outline" onClick={onClose}>Cancelar</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </>
    );
}

// Componente Modal de Observaciones
function ModalObservaciones({ data, notasData, onUpdate }) {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [observacionText, setObservacionText] = useState('');

    useEffect(() => {
        if (isOpen) {
            setObservacionText(notasData[data.matriculaId]?.observaciones || '');
        }
    }, [isOpen, notasData, data.matriculaId]);

    const handleSave = () => {
        onUpdate(data.matriculaId, 'observaciones', observacionText.trim());
        onClose();
    };

    const hasObservacion = Boolean(notasData[data.matriculaId]?.observaciones?.trim());

    return (
        <>
            <Tooltip label={`${hasObservacion ? 'Editar' : 'Agregar'} observaciones`}>
                <IconButton
                    icon={<AddIcon />}
                    onClick={onOpen}
                    colorScheme={hasObservacion ? 'blue' : 'gray'}
                    variant={hasObservacion ? 'solid' : 'outline'}
                    size="sm"
                    isRound
                />
            </Tooltip>

            <Modal isOpen={isOpen} onClose={onClose} size="4xl">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>
                        {hasObservacion ? 'Editar' : 'Agregar'} Observaciones
                        <Text fontSize="sm" color="gray.500" fontWeight="normal">
                            {data.estudiante?.nombres} {data.estudiante?.apellidos}
                        </Text>
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <FormControl>
                            <FormLabel>Observaciones generales</FormLabel>
                            <Textarea
                                placeholder="Escribe las observaciones sobre comportamiento, participación, logros..."
                                value={observacionText}
                                onChange={e => setObservacionText(e.target.value)}
                                minH="200px"
                                focusBorderColor="blue.400"
                            />
                            <Text fontSize="xs" color="gray.500" mt={2}>
                                Caracteres: {observacionText.length}
                            </Text>
                        </FormControl>

                        {observacionText.trim() && (
                            <Box mt={4}>
                                <Text fontSize="sm" fontWeight="semibold" mb={2}>Vista previa:</Text>
                                <Box
                                    p={3}
                                    borderRadius="md"
                                    bg="blue.50"
                                    border="1px"
                                    borderColor="blue.200"
                                    _dark={{ bg: 'blue.900', borderColor: 'blue.600' }}
                                >
                                    <Text fontSize="sm" whiteSpace="pre-wrap">{observacionText}</Text>
                                </Box>
                            </Box>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button colorScheme="blue" mr={3} onClick={handleSave}>
                            Guardar
                        </Button>
                        <Button variant="outline" onClick={onClose}>Cancelar</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </>
    );
}